import React, { useState, lazy, Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { AlertProvider } from './contexts/AlertContext';
import AuthInitializer from './components/AuthInitializer';
import CookieConsent from './components/CookieConsent';
import GoogleAnalytics from './components/GoogleAnalytics';
import Footer from './components/Footer';
import './App.css';

// Lazy load components for better performance
const Product = lazy(() => import('./components/Product'));
const DealDetails = lazy(() => import('./components/DealDetails'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const ProfileModeration = lazy(() => import('./components/ProfileModeration'));
const ReportsManagement = lazy(() => import('./components/ReportsManagement'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const Contact = lazy(() => import('./components/Contact'));
const About = lazy(() => import('./components/About'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const CategoryPage = lazy(() => import('./components/CategoryPage'));
const BusinessProfiles = lazy(() => import('./components/BusinessProfiles'));
const BrowseCategories = lazy(() => import('./components/BrowseCategories'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const BlogPostEditor = lazy(() => import('./components/BlogPostEditor'));
const FAQ = lazy(() => import('./components/FAQ'));
const VoucherCodes = lazy(() => import('./components/VoucherCodes'));
const MerchantRegistration = lazy(() => import('./components/MerchantRegistration'));

// Loading component for suspense fallback
const LoadingFallback = () => (
  <div className="pageFallback">
    <div className="loadingSpinner"></div>
    <p>Loading...</p>
  </div>
);

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const App: React.FC = () => {
  // Cookie preferences state - start with analytics disabled
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytics: false,  // Start with analytics disabled
    marketing: false  // Start with marketing disabled
  });

  // Replace with your actual Google Analytics ID when you get one
  const GA_MEASUREMENT_ID = "G-J7JCEGF3L7";

  return (
    <Provider store={store}>
      <AuthInitializer>
        <AlertProvider>
          <Router>
            <ScrollToTop />
            <Helmet>
              <meta name="description" content="Find the best halal deals, discounts, and offers for Muslim consumers. Share and discover savings on clothing, food, travel, and more!" />
              <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
              <meta name="format-detection" content="telephone=no" />
            </Helmet>
            
            <div className="app-container">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Product />} />
                  <Route path="/deals/:slug" element={<DealDetails />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  
                  {/* Blog Routes */}
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/admin/blog/new" element={<BlogPostEditor />} />
                  <Route path="/admin/blog/edit/:id" element={<BlogPostEditor />} />


                  {/* Admin routes */}
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/reports" element={<ReportsManagement />} />
                  <Route path="/admin/profile-moderation" element={<ProfileModeration />} />
                  
                  <Route path="/users/:id" element={<Suspense fallback={<LoadingFallback />}><UserProfile /></Suspense>} />
                  <Route path="/profile/:username" element={<Suspense fallback={<LoadingFallback />}><UserProfile /></Suspense>} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />

                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/category/:category" element={<CategoryPage />} />
                  <Route path="/businesses" element={<BusinessProfiles />} />
                  <Route path="/browse-categories" element={<BrowseCategories />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/voucher-codes" element={<VoucherCodes />} />
                  <Route path="/merchant-registration" element={<MerchantRegistration />} />

                  {/* Add a catch-all route that redirects to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
              <Footer />
              
              {/* Cookie Consent Banner */}
              <CookieConsent 
                onPreferencesChange={setCookiePreferences}
              />
            </div>

            {/* Google Analytics - only loads if analytics cookies are accepted */}
            <GoogleAnalytics 
              measurementId={GA_MEASUREMENT_ID}
              enabled={cookiePreferences.analytics}
              debug={process.env.NODE_ENV === 'development'}
            />
          </Router>
        </AlertProvider>
      </AuthInitializer>
    </Provider>
  );
};

export default App;